/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import {
	MenuItem,
	TextField,
	Tooltip,
	Checkbox,
	Typography,
	InputAdornment,
	Button,
	Accordion,
	AccordionSummary,
	Grid,
	CardContent,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

import FormTextField from "../../../components/FormTextField/FormTextField";
import { SUPPORTED_LANGUAGES } from "../../../utility/constants";
import { WEB_CHAT_TOOL_TIPS } from "../../../utility/toolTipMessages";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { makeStyles } from "@material-ui/core/styles";
import FormSelectField from "../../../components/FormSelectField/FormSelectField";
import { HeaderCropDialog } from "./HeaderCropDialog";
import { ActionButtons } from "../../../components/Common";

/**
 * This component is used on the Chat configuration page to render the various necessary fields for editing the web chat widgets configuration.
 * @param {Object} selectedConfiguration The currently selected chat configuration to edit.
 * @param {(Object) => void} setSelectedConfiguration Callback method to set the currently selected chat configuration to edit.
 * @param {Array} contactFlows A list of all the contact flows found in AWS Connect.
 * @param {() => void} setFormEdited Callback method to set the form as edited.
 * @param {boolean} formEdited A boolean value of whether or not te values on this form have been edited.
 */

const useStyles = makeStyles(() => ({
	accordion: {
		background: "#eeeeee",
	},
}));

const ConfigurationForm = ({
	selectedConfiguration,
	setSelectedConfiguration,
	contactFlows,
	setFormEdited,
	formEdited,
}) => {
	const setConfigValue = (valueName, value) => {
		setFormEdited(true);
		let updateConfig = { ...selectedConfiguration };
		updateConfig[valueName] = value;
		setSelectedConfiguration(updateConfig);
	};

	const setMultipleConfigValues = (newValues) => {
		setFormEdited(true);
		let updateConfig = { ...selectedConfiguration, ...newValues };
		setSelectedConfiguration(updateConfig);
	};

	const classes = useStyles();
	const [expanded, setExpanded] = React.useState(["amazonConnectSettings"]);

	React.useEffect(() => {
		if (!formEdited && selectedConfiguration) {
			setExpanded(["amazonConnectSettings"]);
		}
	}, [selectedConfiguration, formEdited]);

	const isPanelExpanded = (panel) => {
		return expanded.find((e) => e === panel) === panel ? true : false;
	};

	const handleChange = (panel) => (event, newExpanded) => {
		if (newExpanded) {
			setExpanded(expanded.concat(panel));
		} else {
			const newArray = expanded.filter((e) => e !== panel);
			setExpanded(newArray);
		}
	};

	const onExpandAllClick = () => {
		setExpanded([
			"amazonConnectSettings",
			"widgetAlignment",
			"usernamePrompt",
			"translation",
			"chat-escalations",
			"chatHeader",
			"notification",
			"button",
			"message",
			"attachmentSetup",
		]);
	};

	const onCollapseAllClick = () => {
		setExpanded([]);
	};
	const [cropModalOpen, setCropModalOpen] = React.useState(false);

	return (
		<CardContent>
			{cropModalOpen && (
				<HeaderCropDialog
					selectedConfiguration={selectedConfiguration}
					setConfigValue={setConfigValue}
					setCropModalOpen={setCropModalOpen}
					cropModalOpen={cropModalOpen}
					formEdited={formEdited}
					setFormEdited={setFormEdited}
				/>
			)}
			<ActionButtons
				className="justify-content-end"
				showCancel
				showRemove
				cancelBtnClass="text-dark border-secondary"
				removeBtnClass="text-dark border-secondary"
				onCancel={onExpandAllClick}
				onRemove={onCollapseAllClick}
				cancelLabel="Expand All"
				removeLabel="Collapse All"
			/>
			<div style={{ marginTop: "10px" }}>
				<Accordion expanded={isPanelExpanded("amazonConnectSettings")} onChange={handleChange("amazonConnectSettings")}>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="amazonConnectSettings"
						id="amazonConnectSettings"
						data-test-id="webchat-config-section-amazon-connect-settings"
					>
						Amazon Connect Settings
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<TextField
								label={"Instance ID"}
								defaultValue={selectedConfiguration.instanceId}
								disabled
								className="w-100"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Tooltip arrow interactive placement={"bottom-start"} title={WEB_CHAT_TOOL_TIPS.INSTANCEID}>
												<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
											</Tooltip>
										</InputAdornment>
									),
								}}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							{contactFlows && (
								<TextField
									SelectProps={{
										SelectDisplayProps: {
											"data-test-id": "webchat-config-contact-flow-select",
										},
									}}
									label={"Contact Flow"}
									select
									className="w-100"
									value={selectedConfiguration.contactFlowId || ""}
									error={!selectedConfiguration.contactFlowId || selectedConfiguration.contactFlowId.length === 0}
									onChange={(event) => {
										const edited = selectedConfiguration.contactFlowId !== event.target.value;
										if (edited) setConfigValue("contactFlowId", event.target.value);
									}}
									helperText={
										(!selectedConfiguration.contactFlowId || selectedConfiguration.contactFlowId.length === 0) &&
										"A Contact Flow is required"
									}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<Tooltip arrow interactive placement={"bottom-start"} title={WEB_CHAT_TOOL_TIPS.CONTACT_FLOW}>
													<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
												</Tooltip>
											</InputAdornment>
										),
									}}
								>
									{(contactFlows || []).map((flow) => {
										return (
											<MenuItem key={flow.Id} value={flow.Id} data-test-id="webchat-config-flow-option">
												{flow.Name}
											</MenuItem>
										);
									})}
								</TextField>
							)}
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								inputProps={{
									"data-test-id": "webchat-config-allowed-origin-input",
								}}
								label={"Allowed Origins"}
								formEdited={formEdited}
								required
								styleObject={{ width: "100%" }}
								errorMessage={"Must be at least one allowed origin."}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.allowedOrigins || ""}
								setConfigValue={setConfigValue}
								valueName={"allowedOrigins"}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ALLOWED_ORIGINS}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								inputProps={{
									"data-test-id": "webchat-config-friendly-name-input",
								}}
								styleObject={{ width: "100%" }}
								label={"Configuration Friendly Name"}
								formEdited={formEdited}
								required
								errorMessage={"Friendly name cannot be empty"}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.friendlyName || ""}
								setConfigValue={setConfigValue}
								valueName={"friendlyName"}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.FRIENDLY_NAME}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("widgetAlignment")}
					onChange={handleChange("widgetAlignment")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="widgetAlignment"
						id="widgetAlignment"
						data-test-id="webchat-config-section-widget-alignment"
					>
						Widget Alignment
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormSelectField
								label={"Horizontal Alignment"}
								selectOptions={[
									{ id: "flex-start", label: "Left" },
									{ id: "center", label: "Center" },
									{ id: "flex-end", label: "Right" },
								]}
								startingValue={selectedConfiguration.horizontalAlignment || "flex-end"}
								formEdited={formEdited}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.WIDGET_HORIZONTAL_ALIGNMENT}
								setConfigValue={setConfigValue}
								valueName={"horizontalAlignment"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormSelectField
								label={"Vertical Alignment"}
								selectOptions={[
									{ id: "flex-start", label: "Top" },
									{ id: "center", label: "Center" },
									{ id: "flex-end", label: "Bottom" },
								]}
								startingValue={selectedConfiguration.verticalAlignment || "flex-end"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.WIDGET_VERTICAL_ALIGNMENT}
								setConfigValue={setConfigValue}
								valueName={"verticalAlignment"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Margin"}
								formEdited={formEdited}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.WIDGET_MARGIN}
								startingValue={
									//It's important to check for undefined explicitly for backwards compatibilty and the field being empty
									selectedConfiguration.widgetMargin !== undefined ? selectedConfiguration.widgetMargin : "20px"
								}
								setConfigValue={setConfigValue}
								valueName={"widgetMargin"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("usernamePrompt")}
					onChange={handleChange("usernamePrompt")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="usernamePrompt"
						id="usernamePrompt"
						data-test-id="webchat-config-section-username-prompt-config"
					>
						Username Prompt Configuration
					</AccordionSummary>

					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>Enable Prompt</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.PROMPT_FOR_USERNAME_CHECKBOX}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.promptForUserName || false}
									onChange={(event) => {
										setConfigValue("promptForUserName", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Prompt Message"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								disabled={!selectedConfiguration.promptForUserName}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.PROMPT_FOR_USERNAME_MESSAGE}
								startingValue={selectedConfiguration.userNamePromptMessage || ""}
								setConfigValue={setConfigValue}
								valueName={"userNamePromptMessage"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>Enable Automatic Response</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.USERNAME_ENTRY_THANK_YOU_CHECKBOX}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.userNameEntryThankYou || false}
									onChange={(event) => {
										setConfigValue("userNameEntryThankYou", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Automatic Response Message"}
								formEdited={formEdited}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								disabled={!selectedConfiguration.userNameEntryThankYou}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.USERNAME_ENTRY_THANK_YOU_MESSAGE}
								startingValue={selectedConfiguration.userNameEntryThankYouMessage || ""}
								setConfigValue={setConfigValue}
								valueName={"userNameEntryThankYouMessage"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("translation")}
					onChange={handleChange("translation")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="translation"
						id="translation"
						data-test-id="webchat-config-section-translation"
					>
						Automatic Translation Setup
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>Enable Automatic Translation</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.ENABLE_AUTOMATIC_TRANSLATIONS_CHECKBOX}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.enableTranslation || false}
									onChange={(event) => {
										setConfigValue("enableTranslation", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<TextField
								select
								className="w-100"
								disabled={!selectedConfiguration.enableTranslation}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Tooltip
												arrow
												interactive
												placement={"bottom-start"}
												title={WEB_CHAT_TOOL_TIPS.AGENT_LANGUAGE_SELECT}
											>
												<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
											</Tooltip>
										</InputAdornment>
									),
								}}
								label={"Agent Language"}
								value={selectedConfiguration.agentLanguage || "en"}
								onChange={(event) => {
									const edited = selectedConfiguration.agentLanguage !== event.target.value;
									if (edited) setConfigValue("agentLanguage", event.target.value);
								}}
							>
								{SUPPORTED_LANGUAGES.map((language) => {
									return (
										<MenuItem key={language.languageCode} value={language.languageCode}>
											{language.displayName}
										</MenuItem>
									);
								})}
							</TextField>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Language Button Image"}
								placeholder="Image URL"
								formEdited={formEdited}
								disabled={!selectedConfiguration.enableTranslation}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.LANGUAGE_BUTTON_IMAGE_URL}
								startingValue={selectedConfiguration.languageButtonImageURL || ""}
								setConfigValue={setConfigValue}
								valueName={"languageButtonImageURL"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Language Text Color"}
								formEdited={formEdited}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								disabled={!selectedConfiguration.enableTranslation}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.LANGUAGE_TEXT_COLOR}
								startingValue={selectedConfiguration.languageTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"languageTextColor"}
							/>
						</Grid>
						<Grid item xs={12}>
							<Autocomplete
								multiple
								disableCloseOnSelect
								disabled={!selectedConfiguration.enableTranslation}
								options={SUPPORTED_LANGUAGES}
								getOptionSelected={(option, value) => option.languageCode === value.languageCode}
								getOptionLabel={(option) => option.displayName}
								defaultValue={selectedConfiguration.enabledTranslationLanguages || SUPPORTED_LANGUAGES}
								value={selectedConfiguration.enabledTranslationLanguages || SUPPORTED_LANGUAGES}
								onChange={(_event, newValue) => {
									const edited = selectedConfiguration.enabledTranslationLanguages !== newValue;
									if (edited) setConfigValue("enabledTranslationLanguages", newValue);
								}}
								renderOption={(option, { selected }) => (
									<React.Fragment>
										<Checkbox
											icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
											checkedIcon={<CheckBoxIcon fontSize="small" />}
											style={{ marginRight: 8 }}
											checked={selected}
										/>
										{option.displayName}
									</React.Fragment>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label={"Enabled Translation Languages"}
										className="w-100"
										disabled={!selectedConfiguration.enableTranslation}
										placeholder="Select one or more languages"
										InputLabelProps={{
											shrink: true,
										}}
										InputProps={{
											...params.InputProps,
											style: {
												minHeight: 32,
											},
											startAdornment: (
												<>
													<InputAdornment position="start">
														<Tooltip
															arrow
															interactive
															placement={"bottom-start"}
															title={WEB_CHAT_TOOL_TIPS.ENABLED_TRANSLATION_LANGUAGES_SELECT}
														>
															<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
														</Tooltip>
													</InputAdornment>
													{params.InputProps.startAdornment}
												</>
											),
										}}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("chat-escalations")}
					onChange={handleChange("chat-escalations")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="chat-escalations"
						id="chat-escalations"
						data-test-id="webchat-config-section-chat-escalations"
					>
						Video Escalations and Screen Sharing
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>
									Enable Video Escalations and Screen Sharing
								</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.CHAT_ESCALATIONS_ENABLE_FEATURE}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.enableChatEscalations || false}
									onChange={(event) => {
										setConfigValue("enableChatEscalations", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Escalation Options Button Image"}
								placeholder="Image URL"
								formEdited={formEdited}
								disabled={!selectedConfiguration.enableChatEscalations}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ESCALATION_OPTIONS_BUTTON_IMAGE_URL}
								startingValue={selectedConfiguration.escalationOptionsImageURL || ""}
								setConfigValue={setConfigValue}
								valueName={"escalationOptionsImageURL"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Video Calls Background Color"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.enableChatEscalations}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ESCALATION_VIDEO_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.videoTileBackgroundColor || "#d1d5db"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"videoTileBackgroundColor"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("chatHeader")}
					onChange={handleChange("chatHeader")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="chatHeader-content"
						id="chatHeader"
						data-test-id="webchat-config-section-chat-header"
					>
						Chat Header
					</AccordionSummary>

					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormTextField
								label="Header Background Color"
								formEdited={formEdited}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.HEADER_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.headerBackgroundColor || "#941012"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"headerBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6} className="d-flex justify-content-between align-items-end">
							<FormTextField
								label={"Header Background Image"}
								formEdited={formEdited}
								styleObject={{ width: "70%" }}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.HEADER_BACKGROUND_IMAGE}
								startingValue={selectedConfiguration.headerBackgroundImage || ""}
								setConfigValue={(valueName, newValue) =>
									setMultipleConfigValues({ [valueName]: newValue, headerImageCropSettings: undefined })
								}
								valueName={"headerBackgroundImage"}
								placeholder="Image URL"
							/>
							<Button
								variant="outlined"
								size="small"
								className="w-30"
								disabled={!selectedConfiguration.headerBackgroundImage}
								onClick={() => setCropModalOpen(true)}
							>
								Preview
							</Button>
							{/* <div style={{ margin: "43px 10px 0px 0px" }}>
							</div> */}
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Close Button Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.CLOSE_BUTTON_IMAGE}
								startingValue={selectedConfiguration.closeButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"closeButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Hide Button Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.HIDE_BUTTON_IMAGE}
								startingValue={selectedConfiguration.hideButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"hideButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Expand Button Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.EXPAND_BUTTON_IMAGE}
								startingValue={selectedConfiguration.expandButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"expandButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Collapse Button Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.COLLAPSE_EXPAND_BUTTON_IMAGE}
								startingValue={selectedConfiguration.collapseExpandButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"collapseExpandButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>Enable Transcript Download</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.ENABLE_DOWNLOAD_BUTTON_CHECKBOX}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.enableDownloadButton || false}
									onChange={(event) => {
										setConfigValue("enableDownloadButton", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Transcript Download Image"}
								placeholder="Image URL"
								formEdited={formEdited}
								disabled={!selectedConfiguration.enableDownloadButton}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.DOWNLOAD_BUTTON_IMAGE_URL}
								startingValue={selectedConfiguration.downloadButtonImageURL || ""}
								setConfigValue={setConfigValue}
								valueName={"downloadButtonImageURL"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Title Text"}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.TITLE_TEXT}
								formEdited={formEdited}
								styleObject={{ width: "100%" }}
								setEdited={setFormEdited}
								startingValue={selectedConfiguration.title || ""}
								setConfigValue={setConfigValue}
								valueName={"title"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Title Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.TITLE_TEXT_COLOR}
								startingValue={selectedConfiguration.headerTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"headerTextColor"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("notification")}
					onChange={handleChange("notification")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="notification-content"
						id="notification"
						data-test-id="webchat-config-section-notification"
					>
						Notification
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>Enable Customer Notification</Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.ENABLE_AUDIO_NOTIFICATION}
								>
									<i
										style={{ height: "15px", margin: "12px 12px 12px 0", color: "gray" }}
										className="fas fa-info-circle"
									/>
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.enableAudioNotification || false}
									onChange={(event) => {
										setConfigValue("enableAudioNotification", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Customer Notification"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								disabled={!selectedConfiguration.enableAudioNotification}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.USER_NOTIFICATION_URL}
								startingValue={selectedConfiguration.userNotificationURL || ""}
								setConfigValue={setConfigValue}
								valueName={"userNotificationURL"}
								placeholder="Audio URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Mute Button Image"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.enableAudioNotification}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.MUTE_MENU_BUTTON_IMAGE}
								startingValue={selectedConfiguration.muteMenuButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"muteMenuButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								label={"Unmute Button Image"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.enableAudioNotification}
								setEdited={setFormEdited}
								styleObject={{ width: "100%" }}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.UNMUTE_MENU_BUTTON_IMAGE}
								startingValue={selectedConfiguration.unmuteMenuButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"unmuteMenuButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion style={{ marginTop: "10px" }} expanded={isPanelExpanded("button")} onChange={handleChange("button")}>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="button-content"
						id="button-header"
						data-test-id="webchat-config-section-buttons"
					>
						Buttons
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Start Chat Button Image"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.START_CHAT_IMAGE}
								startingValue={selectedConfiguration.startChatButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"startChatButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Start Chat Button Text"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.START_BUTTON_TEXT}
								startingValue={selectedConfiguration.startButtonText || ""}
								setConfigValue={setConfigValue}
								valueName={"startButtonText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Start Button Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.START_BUTTON_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.buttonBackgroundColor || "#941012"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"buttonBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Start Button Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.START_BUTTON_TEXT_COLOR}
								startingValue={selectedConfiguration.buttonTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"buttonTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Hang Up Chat Button Text"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.HANG_UP_BUTTON_TEXT}
								startingValue={selectedConfiguration.hangupButtonText || ""}
								setConfigValue={setConfigValue}
								valueName={"hangupButtonText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Hang Up Button Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.HANG_UP_BUTTON_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.hangupButtonBackgroundColor || "#941012"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"hangupButtonBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Hang Up Button Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.HANG_UP_BUTTON_TEXT_COLOR}
								startingValue={selectedConfiguration.hangupButtonTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"hangupButtonTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Resume Chat Button Text"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.RESUME_BUTTON_TEXT}
								startingValue={selectedConfiguration.resumeButtonText || ""}
								setConfigValue={setConfigValue}
								valueName={"resumeButtonText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Resume Button Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.RESUME_BUTTON_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.resumeButtonBackgroundColor || "#941012"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"resumeButtonBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Resume Button Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.RESUME_BUTTON_TEXT_COLOR}
								startingValue={selectedConfiguration.resumeButtonTextColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"resumeButtonTextColor"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("message")}
					onChange={handleChange("message")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="message-content"
						id="message"
						data-test-id="webchat-config-section-message"
					>
						Message
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"System Message Label"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.SYSTEM_MESSAGE_LABEL}
								startingValue={selectedConfiguration.systemMessageLabel || ""}
								setConfigValue={setConfigValue}
								valueName={"systemMessageLabel"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"BOT Message Label"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.BOT_MESSAGE_LABEL}
								startingValue={selectedConfiguration.botMessageLabel || ""}
								setConfigValue={setConfigValue}
								valueName={"botMessageLabel"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Response Message Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.RESPONSE_MESSAGE_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.responseMessageBackgroundColor || "#f4f7f9"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"responseMessageBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Response Message Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.RESPONSE_MESSAGE_TEXT_COLOR}
								startingValue={selectedConfiguration.responseMessageTextColor || "#000000"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"responseMessageTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Customer Message Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.USER_MESSAGE_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.userMessageBackgroundColor || "#a3eaf7"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"userMessageBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Customer Message Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.USER_MESSAGE_TEXT_COLOR}
								startingValue={selectedConfiguration.userMessageTextColor || "#000000"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"userMessageTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Message Viewer Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.MESSAGE_VIEWER_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.messageViewBackgroundColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"messageViewBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Input Message Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.NEW_MESSAGE_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.newMessageBackgroundColor || "#f4f7f9"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"newMessageBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Input Message Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.NEW_MESSAGE_TEXT_COLOR}
								startingValue={selectedConfiguration.newMessageTextColor || "#000000"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"newMessageTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Send Message Button"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.SEND_MESSAGE_BUTTON_IMAGE}
								startingValue={selectedConfiguration.sendMessageButtonImage || ""}
								setConfigValue={setConfigValue}
								valueName={"sendMessageButtonImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Placeholder Text"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.PLACEHOLDER_TEXT}
								startingValue={selectedConfiguration.placeholderText || "Type a question"}
								setConfigValue={setConfigValue}
								valueName={"placeholderText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Placeholder Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.PLACEHOLDER_COLOR}
								startingValue={selectedConfiguration.placeholderColor || "#000000"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"placeholderColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Conversation Separator Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.CONVERSATION_SEPARATOR_COLOR}
								startingValue={selectedConfiguration.conversationSeparatorColor || "#808080"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"conversationSeparatorColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Connecting Message Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.CONNECTING_MESSAGE_TEXT_COLOR}
								startingValue={selectedConfiguration.connectingMessageTextColor || "#80bb80"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"connectingMessageTextColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Interactive Message Button Background Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.INTERACTIVE_MESSAGE_BUTTON_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.interactiveMessageButtonBackgroundColor || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"interactiveMessageButtonBackgroundColor"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Interactive Message Button Text Color"}
								formEdited={formEdited}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.INTERACTIVE_MESSAGE_BUTTON_TEXT_COLOR}
								startingValue={selectedConfiguration.interactiveMessageButtonTextColor || "#000000"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"interactiveMessageButtonTextColor"}
							/>
						</Grid>
					</Grid>
				</Accordion>
				<Accordion
					style={{ marginTop: "10px" }}
					expanded={isPanelExpanded("attachmentSetup")}
					onChange={handleChange("attachmentSetup")}
				>
					<AccordionSummary
						className={classes.accordion}
						expandIcon={<ExpandMoreIcon />}
						aria-controls="attachmentSetup-content"
						id="attachmentSetup"
						data-test-id="webchat-config-section-attachment-setup"
					>
						Attachment Setup
					</AccordionSummary>
					<Grid container spacing={3} className="p-3">
						<Grid item xs={12} lg={6}>
							<div className="w-100">
								<Typography style={{ fontSize: 12, color: "gray" }}>Enable Attachments </Typography>
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={WEB_CHAT_TOOL_TIPS.ENABLE_ATTACHMENTS_CHECKBOX}
								>
									<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
								</Tooltip>
								<Checkbox
									checked={selectedConfiguration.attachmentsEnabled || false}
									onChange={(event) => {
										setConfigValue("attachmentsEnabled", event.target.checked);
									}}
								/>
							</div>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Attachment Button Image"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.attachmentsEnabled}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ATTACHMENT_BUTTON_IMAGE}
								startingValue={selectedConfiguration.attachmentImage || ""}
								setConfigValue={setConfigValue}
								valueName={"attachmentImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Chip Background Color"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.attachmentsEnabled}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ATTACHMENT_CHIP_BACKGROUND_COLOR}
								startingValue={selectedConfiguration.attachmentChipBackground || "#941012"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"attachmentChipBackground"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Chip Text Color"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.attachmentsEnabled}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ATTACHMENT_CHIP_TEXT_COLOR}
								startingValue={selectedConfiguration.attachmentChipText || "#ffffff"}
								type={"color"}
								setConfigValue={setConfigValue}
								valueName={"attachmentChipText"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Sending Attachment Phrase"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.attachmentsEnabled}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.SENDING_ATTACHMENTS_PHRASE}
								startingValue={selectedConfiguration.sendingAttachmentsPhrase || ""}
								setConfigValue={setConfigValue}
								valueName={"sendingAttachmentsPhrase"}
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Attachment Warning Image"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.attachmentsEnabled}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.ATTACHMENT_WARNING_IMAGE}
								startingValue={selectedConfiguration.attachmentWarningImage || ""}
								setConfigValue={setConfigValue}
								valueName={"attachmentWarningImage"}
								placeholder="Image URL"
							/>
						</Grid>
						<Grid item xs={12} lg={6}>
							<FormTextField
								styleObject={{ width: "100%" }}
								label={"Download Attachment Image"}
								formEdited={formEdited}
								disabled={!selectedConfiguration.attachmentsEnabled}
								setEdited={setFormEdited}
								toolTipMessage={WEB_CHAT_TOOL_TIPS.DOWNLOAD_ATTACHMENT_IMAGE}
								startingValue={selectedConfiguration.downloadAttachmentImage || ""}
								setConfigValue={setConfigValue}
								valueName={"downloadAttachmentImage"}
								placeholder="Image URL"
							/>
						</Grid>
					</Grid>
				</Accordion>
			</div>
		</CardContent>
	);
};

export default ConfigurationForm;
